/**
 * Единственное определение ключа метки в системе.
 *
 * SQL отдаёт сырые строки, сведение живёт только здесь: два места нормализации
 * (JS + SQL) расходятся молча — так уже разошлось округление балла (#649).
 */

// ’ ‘ ‚ ʼ ` ´ — один и тот же апостроф в «зв’язку», набранный по-разному (и
// одинарные кавычки, которые на письме от него не отличить).
//
// Заменяются ДО NFKC, и порядок здесь — не вкус: NFKC раскладывает ´ (U+00B4)
// в ПРОБЕЛ + U+0301, после чего «зв´язку» уже не свести ничем — в ключе
// остаётся пробел посреди слова.
const APOSTROPHES = /[\u2019\u2018\u201A\u02BC\u0060\u00B4]/g;

// « » “ ” „ ‟ ‹ › — один знак для ключа. ASR и модель чередуют начертания
// свободно: без этого «ТОВ «Агро»», «ТОВ "Агро"» и «ТОВ “Агро”» — три разных
// конкурента, пока глоссарий не перечислит алиасом каждое начертание.
const DOUBLE_QUOTES = /[\u00AB\u00BB\u201C\u201D\u201E\u201F\u2039\u203A]/g;

function isWrapped(value: string): boolean {
  if (value.length < 2) return false;
  const first = value[0];
  return (first === '"' || first === "'") && value[value.length - 1] === first;
}

/**
 * Снимает ВСЕ слои обрамляющих кавычек. Внутренние остаются: «ТОВ «Агро» Плюс»
 * без них — другая строка, а не та же компания.
 *
 * Все слои, а не один: ключ обязан быть неподвижной точкой,
 * `normalizeLabelKey(normalizeLabelKey(x)) === normalizeLabelKey(x)`. Иначе
 * «ціна» и «"ціна"» — разные ключи, а хешер меток, нормализующий ещё раз, даёт
 * им один хэш: читатель таблицы соответствий отдавал строку только одному
 * ключу, а писатель сохранял только первую классификацию (#905).
 *
 * Намеренно НЕ парное правило. Парное отказалось бы снимать кавычки с
 * «ТОВ «Агро»» (закрывающая встречается внутри) и оставило бы его врозь с
 * ТОВ «Агро» — а вложенные юр. названия у конкурентов встречаются постоянно.
 * Цена — некрасивый ключ у редкого «А» и «Б», который всё равно сводит все
 * свои написания в один.
 */
function stripWrappingQuotes(value: string): string {
  let current = value;
  while (isWrapped(current)) current = current.slice(1, -1).trim();
  return current;
}

/** Единый апостроф, NFKC, единая двойная кавычка, схлопнутые пробелы. */
function unifyPunctuation(raw: string): string {
  return raw
    .replace(APOSTROPHES, "'")
    .normalize('NFKC')
    .replace(DOUBLE_QUOTES, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Ключ группировки: единая пунктуация, без обрамляющих кавычек, нижний регистр.
 * Пустая строка — «ключа нет». Латиницу с кириллицей НЕ сводит: угаданное
 * слияние двух разных компаний хуже двух строк — это работа глоссария.
 */
export function normalizeLabelKey(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return stripWrappingQuotes(unifyPunctuation(raw)).toLowerCase();
}

/**
 * Написание для показа: без обрамляющих кавычек и лишних пробелов, но с
 * исходным регистром и исходным начертанием внутренних кавычек. У одного ключа
 * таких написаний несколько («Amazone», «AMAZONE») — выбрать одно
 * детерминированно обязан тот, кто сводит (агрегат), а не тот, кто читает строку.
 */
export function cleanLabelForDisplay(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  // Апостроф унифицируется и здесь — по той же причине порядка, что в ключе.
  const collapsed = raw
    .replace(APOSTROPHES, "'")
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim();
  // Обрамление определяется по строке с едиными кавычками, а срезается с этой:
  // замена кавычек идёт знак на знак, поэтому срез и обрезка пробелов
  // сдвигают обе строки одинаково. Слоёв снимается столько же, сколько у ключа.
  let unified = collapsed.replace(DOUBLE_QUOTES, '"');
  let display = collapsed;
  while (isWrapped(unified)) {
    unified = unified.slice(1, -1).trim();
    display = display.slice(1, -1).trim();
  }
  return display;
}
